import type { VfxTier } from '@/utils/vfxManager';
import type { SiteThemeId } from './siteTheme';
import type { BackgroundConfig } from './siteBackgroundSettings';
import { disposeNeuronField, NEURON_REST, neuronLayerTransform, paintNeuronFrame, prepareNeuronField, type PreparedNeuronField } from './biologicalField';
import { mountPointField } from './pointField';

export type SiteBackgroundMode = 'featured' | 'quiet' | 'off';

export function getSiteBackgroundMode(pathname: string): SiteBackgroundMode {
  if (!pathname) return 'off';
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/about' || path === '/news' || path.startsWith('/news/')) return 'featured';
  if (['/profile', '/projects', '/vacancies'].includes(path) || /^\/users\/\d+$/.test(path)) return 'quiet';
  return 'off';
}

export interface BackgroundOptions {
  config: BackgroundConfig;
  theme: SiteThemeId;
  background: string;
  ink: string;
  tier: VfxTier;
  motion: boolean;
  preview?: boolean;
}

/** Предпросмотр изолирован от страницы; публичный фон не управляет нативной прокруткой. */
export function mountSiteBackground(canvas: HTMLCanvasElement, options: BackgroundOptions): () => void {
  canvas.dataset.backgroundEffect = options.config.effect;
  if (options.config.effect === 'points') {
    delete canvas.dataset.neuronCount;
    return mountPointField(canvas, { tier: options.tier, color: options.ink, motion: options.motion && !options.preview });
  }
  const doc = canvas.ownerDocument;
  const view = doc.defaultView;
  const ctx = canvas.getContext('2d');
  if (!view || !ctx) return () => {};
  const reduced = view.matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = view.matchMedia('(hover: hover) and (pointer: fine)');
  let width = 0;
  let height = 0;
  let field: PreparedNeuronField | null = null;
  let frame: number | null = null;
  let visible = true;
  let lastTime = 0;
  let lastPaintTime = 0;
  let sceneTime = 2400;
  let cursor: { x: number; y: number } | null = null;
  const pose = { ...NEURON_REST };
  const target = { ...pose };
  const interaction = options.preview ? canvas : view;

  const draw = () => {
    if (!field) return;
    let nearest: number | null = null;
    let distance = Math.min(width, height) * .26;
    if (cursor) field.cells.forEach((cell, index) => {
      const t = neuronLayerTransform(cell.layer, pose, field!.depth, sceneTime);
      const x = cell.cx - width / 2; const y = cell.cy - height / 2;
      const d = Math.hypot(cursor!.x - (width / 2 + t.x + x * Math.cos(t.angle) - y * Math.sin(t.angle)),
        cursor!.y - (height / 2 + t.y + x * Math.sin(t.angle) + y * Math.cos(t.angle)));
      if (d < distance) { nearest = index; distance = d; }
    });
    paintNeuronFrame(ctx, field, pose, sceneTime, nearest);
  };
  const cancel = () => {
    if (frame !== null) view.cancelAnimationFrame(frame);
    frame = null;
  };
  const animate = (time: number) => {
    frame = view.requestAnimationFrame(animate);
    const interval = width <= 768 || options.tier !== 'max' ? 50 : 1000 / 30;
    const delta = time - lastTime;
    if (delta < interval) return;
    lastTime = time - delta % interval;
    const elapsed = Math.min(time - lastPaintTime, 100);
    lastPaintTime = time;
    sceneTime += elapsed;
    const ease = 1 - Math.exp(-elapsed / 220);
    for (const key of ['x', 'y', 'scroll'] as const) pose[key] += (target[key] - pose[key]) * ease;
    draw();
  };
  const onScroll = () => {
    const scroll = Math.max(0, view.scrollY);
    target.scroll = scroll / (scroll + Math.max(1, view.innerHeight));
  };
  const resetPointer = () => { cursor = null; target.x = target.y = 0; };
  const onPointer = (event: Event) => {
    const input = event as PointerEvent;
    if (!pointer.matches || input.pointerType === 'touch') return;
    const rect = canvas.getBoundingClientRect();
    cursor = { x: input.clientX - rect.left, y: input.clientY - rect.top };
    target.x = Math.max(-1, Math.min(1, cursor.x / width * 2 - 1));
    target.y = Math.max(-1, Math.min(1, cursor.y / height * 2 - 1));
  };
  const onPointerOut = (event: Event) => { if (options.preview || !(event as PointerEvent).relatedTarget) resetPointer(); };
  const removeInteraction = () => {
    view.removeEventListener('scroll', onScroll);
    interaction.removeEventListener('pointermove', onPointer);
    interaction.removeEventListener('pointerout', onPointerOut);
    view.removeEventListener('blur', resetPointer);
  };
  const configure = () => {
    cancel(); removeInteraction(); resetPointer();
    const enabled = options.motion && options.tier !== 'off' && !reduced.matches;
    Object.assign(pose, NEURON_REST); Object.assign(target, NEURON_REST);
    if (doc.hidden || !visible) { canvas.dataset.motion = 'hidden'; return; }
    canvas.dataset.motion = enabled ? 'living' : 'static';
    if (!enabled) sceneTime = 2400;
    if (enabled && !options.preview) { onScroll(); pose.scroll = target.scroll; }
    draw();
    if (!enabled) return;
    if (!options.preview) view.addEventListener('scroll', onScroll, { passive: true });
    if (pointer.matches) {
      interaction.addEventListener('pointermove', onPointer, { passive: true });
      interaction.addEventListener('pointerout', onPointerOut, { passive: true });
      view.addEventListener('blur', resetPointer);
    }
    lastTime = lastPaintTime = view.performance.now();
    frame = view.requestAnimationFrame(animate);
  };
  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    const nextWidth = Math.max(1, Math.round(rect.width)); const nextHeight = Math.max(1, Math.round(rect.height));
    if (field && width === nextWidth && height === nextHeight) return;
    cancel();
    width = nextWidth; height = nextHeight;
    if (field) disposeNeuronField(field);
    const dpr = Math.min(view.devicePixelRatio || 1, width <= 768 ? 1 : 1.5, Math.sqrt(3_000_000 / (width * height)));
    canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    field = prepareNeuronField(doc, width, height, options.config, options.theme, options.background, options.tier, options.preview);
    canvas.dataset.neuronCount = String(field.cells.length);
    configure();
  };
  resize();
  const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
  observer?.observe(canvas);
  const intersection = options.preview && typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
    const next = entries[0]?.isIntersecting ?? false;
    if (visible !== next) { visible = next; configure(); }
  }) : null;
  intersection?.observe(canvas);
  view.addEventListener('resize', resize, { passive: true });
  doc.addEventListener('visibilitychange', configure);
  reduced.addEventListener('change', configure);
  pointer.addEventListener('change', configure);
  return () => {
    cancel(); removeInteraction(); observer?.disconnect(); intersection?.disconnect();
    view.removeEventListener('resize', resize);
    doc.removeEventListener('visibilitychange', configure);
    reduced.removeEventListener('change', configure);
    pointer.removeEventListener('change', configure);
    if (field) disposeNeuronField(field);
    canvas.width = canvas.height = 1;
  };
}
